import React from 'react';
import { Link, useLocation } from 'react-router-dom';

/* Map of route segment → display label */
const breadcrumbNameMap = {
  'dashboard': 'Dashboard',
  'api-master': 'API Master',
  'product-master': 'Product Master',
  'products': 'Product Master',
  'equipment-master': 'Equipment Master',
  'equipment-trains': 'Equipment Trains',
  'equipment': 'Equipment',
  'users': 'User Management',
  'cleaning-config': 'Cleaning Config',
  'analytical-methods': 'Analytical Methods',
  'vrl': 'VRL Studies',
  'worst-case': 'Worst Case',
  'maco': 'MACO Calculation',
  'sampling-plans': 'Sampling Plan & Acceptance Criteria',
  'acceptance-criteria': 'Sampling Plan & Acceptance Criteria',
  'results': 'Results',
  'training': 'Training',
  'ccv': 'CCV',
  'revalidation': 'Revalidation',
  'audit-trail': 'Audit Trail',
  'cleaning-validation-settings': 'CV Coverage',
  'gap-assessment': 'Gap Assessment',
  'admin-settings': 'Admin Settings',
  'settings': 'App Configuration',
  'commercial-setup': 'Commercial Setup',
  'integrations': 'Integration Hub',
  'risk-assessment': 'Risk Assessment',
  'alerts-notifications': 'Alerts & Notifications',
  'approval-flow': 'Approval Flow',
};

/* Which sidebar group does each route belong to?
   `path` points at the group's first child page (mirrors Layout.js menuItems
   order) since there's no dedicated landing page per group. */
const routeGroupMap = {
  'api-master':              { label: 'Master',   path: '/product-master' },
  'product-master':          { label: 'Master',   path: '/product-master' },
  'products':                { label: 'Master',   path: '/product-master' },
  'equipment-master':        { label: 'Master',   path: '/product-master' },
  'equipment-trains':        { label: 'Master',   path: '/product-master' },
  'users':                   { label: 'Master',   path: '/product-master' },
  'worst-case':              { label: 'Reports',  path: '/worst-case' },
  'gap-assessment':          { label: 'Assessments', path: '/risk-assessment' },
  'risk-assessment':         { label: 'Assessments', path: '/risk-assessment' },
  'maco':                    { label: 'Reports',  path: '/worst-case' },
  'sampling-plans':          { label: 'Reports',  path: '/worst-case' },
  'acceptance-criteria':     { label: 'Reports',  path: '/worst-case' },
  'cleaning-validation-settings': { label: 'Assessments', path: '/risk-assessment' },
  'admin-settings':          { label: 'Settings', path: '/settings' },
  'settings':                { label: 'Settings', path: '/settings' },
  'commercial-setup':        { label: 'Settings', path: '/settings' },
  'integrations':            { label: 'Settings', path: '/settings' },
};

const Breadcrumbs = () => {
  const location = useLocation();
  const pathnames = location.pathname.split('/').filter((x) => x);

  if (pathnames.length === 0) return null;
  const isDashboardRoot = pathnames.length === 1 && pathnames[0] === 'dashboard';

  // Build the crumb list, optionally injecting a group crumb. On the
  // dashboard itself there's nothing to drill into, so the trail is just
  // the "Home" crumb below — same markup/padding as every other page so
  // the bar's height never shifts between routes.
  const crumbs = [];
  if (!isDashboardRoot) {
    const firstSegment = pathnames[0];
    const group = routeGroupMap[firstSegment];
    if (group) {
      crumbs.push({ label: group.label, to: group.path, isGroup: true });
    }
    pathnames.forEach((value, index) => {
      const to = `/${pathnames.slice(0, index + 1).join('/')}`;
      const label = breadcrumbNameMap[value] || value.charAt(0).toUpperCase() + value.slice(1).replace(/-/g, ' ');
      crumbs.push({ label, to, isGroup: false });
    });
  }

  return (
    <nav aria-label="breadcrumb" className="breadcrumb-bar" style={{ overflowX: 'auto' }}>
      <ol className="breadcrumb" style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', gap: '0.25rem', alignItems: 'center', flexWrap: 'nowrap', whiteSpace: 'nowrap', fontSize: 'var(--fs-xs)', lineHeight: 1 }}>
        <li className="breadcrumb-item" style={{ display: 'inline-flex', alignItems: 'center' }} aria-current={isDashboardRoot ? 'page' : undefined}>
          {isDashboardRoot ? <span>Home</span> : <Link to="/dashboard">Home</Link>}
        </li>
        {crumbs.map((crumb, index) => {
          const isLast = index === crumbs.length - 1;
          return (
            <React.Fragment key={`${crumb.label}-${index}`}>
              <li className="breadcrumb-separator" style={{ display: 'inline-flex', alignItems: 'center', margin: 0, opacity: 0.5, lineHeight: 1 }}>/</li>
              <li className={`breadcrumb-item ${isLast ? 'active' : ''}`} style={{ display: 'inline-flex', alignItems: 'center' }} aria-current={isLast ? 'page' : undefined}>
                {isLast || !crumb.to ? (
                  <span style={isLast ? {} : { color: 'var(--text-secondary)' }}>{crumb.label}</span>
                ) : (
                  <Link to={crumb.to}>{crumb.label}</Link>
                )}
              </li>
            </React.Fragment>
          );
        })}
      </ol>
    </nav>
  );
};

export default Breadcrumbs;
